import { createContext, useContext, useEffect, useState } from "react";
import { getRupturaResumo, type RupturaDimensao, type RupturaFiltros, type RupturaResumo } from "@/api/ruptura.service";

// Cache das buscas da tela de ruptura: reabrir um "Ver por", voltar a uma aba ou reabrir
// a lista de produtos reaproveita o que já veio. Vive na RupturaPage e é trocado no
// "Recarregar". Erro não fica no cache, para a próxima tentativa buscar de novo.
type Entrada = { promessa: Promise<unknown>; pronto: boolean; dados?: unknown };
export type CacheRuptura = Map<string, Entrada>;

export const CacheRupturaContext = createContext<CacheRuptura>(new Map());

// Lojas (erp_code) que entram nas buscas, definidas no seletor "Lojas" da tela.
export const LojasRupturaContext = createContext<string[]>([]);

export type BuscaRuptura<T> = { dados: T | null; erro: string | null; loading: boolean };

// chave null = nada a buscar (sem cliente). A chave identifica a busca: buscar só é
// chamado quando ela ainda não está no cache.
export function useRupturaBusca<T>(chave: string | null, buscar: () => Promise<T>, mensagemErro: string): BuscaRuptura<T> {
  const cache = useContext(CacheRupturaContext);
  const [resposta, setResposta] = useState<{ cache: CacheRuptura; chave: string; dados: T | null; erro: string | null } | null>(
    null,
  );

  useEffect(() => {
    if (chave === null) return;
    let entrada = cache.get(chave);
    if (!entrada) {
      const nova: Entrada = { promessa: buscar(), pronto: false };
      nova.promessa.then(
        (dados) => {
          nova.dados = dados;
          nova.pronto = true;
        },
        () => cache.delete(chave),
      );
      cache.set(chave, nova);
      entrada = nova;
    }
    let cancelado = false;
    entrada.promessa.then(
      (dados) => !cancelado && setResposta({ cache, chave, dados: dados as T, erro: null }),
      (e) => !cancelado && setResposta({ cache, chave, dados: null, erro: e?.message ?? mensagemErro }),
    );
    return () => {
      cancelado = true;
    };
    // buscar e mensagemErro são definidos pela chave.
  }, [cache, chave]);

  if (chave === null) return { dados: null, erro: null, loading: false };
  // Já em cache: sem piscar o carregando ao reabrir.
  const emCache = cache.get(chave);
  if (emCache?.pronto) return { dados: emCache.dados as T, erro: null, loading: false };
  if (resposta && resposta.cache === cache && resposta.chave === chave) {
    return { dados: resposta.dados, erro: resposta.erro, loading: false };
  }
  return { dados: null, erro: null, loading: true };
}

// Todos os campos, na mesma ordem: {} e { cod_empresa: null } caem na mesma chave.
const chaveDoFiltro = (f: RupturaFiltros) =>
  JSON.stringify([f.cod_empresa, f.cod_categoria, f.cod_comprador, f.cod_fornecedor, f.cod_problema, f.cod_responsavel, f.cod_solucao].map((v) => v ?? null));

// lojasDaTela: só a própria RupturaPage passa (ela é quem fornece o contexto, não o lê).
export const useRupturaResumo = (
  dimensao: RupturaDimensao,
  filtro: RupturaFiltros,
  clientId: string | undefined,
  lojasDaTela?: string[],
) => {
  const lojasDoContexto = useContext(LojasRupturaContext);
  const lojas = lojasDaTela ?? lojasDoContexto;
  // Sem loja selecionada não há o que buscar.
  return useRupturaBusca<RupturaResumo[]>(
    clientId && lojas.length > 0 ? `resumo:${clientId}:${lojas.join(",")}:${dimensao}:${chaveDoFiltro(filtro)}` : null,
    () => getRupturaResumo(dimensao, filtro, clientId!, lojas),
    "Não foi possível carregar o resumo.",
  );
};
